import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Heart, Compass, Map, TrendingUp, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

// Hero background image, Nia in foreground with 4 team members behind
const TEAM_HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/962a0cec-3c5b-4215-b5e1-da1eeac573d5/images/1dcaea0925b9d3b82a51ef164b520877c675edfdd0a7d866bc047411ae367276.png";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

// Team member portraits, CONSISTENT across all sections
// Emotional Readiness Guide: Blonde white woman
// Career Discovery Coach: Asian male  
// Resource Navigator: African American woman
// Transition Specialist: Hispanic male
// Nia: ORIGINAL image (restored)
const PORTRAITS = {
  emotionalReadinessGuide: "https://static.prod-images.emergentagent.com/jobs/962a0cec-3c5b-4215-b5e1-da1eeac573d5/images/456d3e3b5a20b09d6e40827e5ebebcbc15e7520e4ae3bb9e1c8c28e1d8efb6b4.png",
  careerDiscoveryCoach: "https://static.prod-images.emergentagent.com/jobs/962a0cec-3c5b-4215-b5e1-da1eeac573d5/images/5dec65f8f01f6846cdf575f539e1d918624465c394aed669397bd84ad1d74e21.png",
  resourceNavigator: "https://static.prod-images.emergentagent.com/jobs/962a0cec-3c5b-4215-b5e1-da1eeac573d5/images/2ca563e4aacc9a0bcb642c3a081d9804112e1bff959ce8f8586ba02780e32a03.png",
  transitionSpecialist: "https://static.prod-images.emergentagent.com/jobs/962a0cec-3c5b-4215-b5e1-da1eeac573d5/images/75945896fa718a2b288733eb33bb8bd96a6e2f4ff01fe88c78032c3925191283.png",
  nia: "https://static.prod-images.emergentagent.com/jobs/962a0cec-3c5b-4215-b5e1-da1eeac573d5/images/7f077cf364bd400754892dc84fc85c0653f73358f060e8be8962d973e4114fbc.png"
};

const teamMembers = [
  {
    name: "Emotional Readiness Guide",
    icon: Heart,
    color: "from-rose-400 to-rose-600",
    portrait: PORTRAITS.emotionalReadinessGuide,
    description: "Helps you process transitions and build emotional readiness, drawing on decades of proven transformation frameworks.",
    capabilities: [
      "Emotional processing support",
      "Stress and anxiety management",
      "Building resilience",
      "Guided self reflection"
    ]
  },
  {
    name: "Career Discovery Coach",
    icon: Compass,
    color: "from-amber-400 to-orange-500",
    portrait: PORTRAITS.careerDiscoveryCoach,
    description: "Helps you find career paths that fit who you actually are, not who you think you should be, who others say you should be, or just what you've done before.",
    capabilities: [
      "Strengths assessment",
      "Values alignment",
      "Career path exploration",
      "Skills gap analysis"
    ]
  },
  {
    name: "Resource Navigator",
    icon: Map,
    color: "from-emerald-400 to-teal-500",
    portrait: PORTRAITS.resourceNavigator,
    description: "Finds scholarships, grants, training programs, childcare, equipment lending libraries, and resources you didn't know existed.",
    capabilities: [
      "Scholarship & grant discovery",
      "Training program matching",
      "Local resource identification",
      "Benefit optimization"
    ]
  },
  {
    name: "Transition Specialist",
    icon: TrendingUp,
    color: "from-violet-400 to-purple-600",
    portrait: PORTRAITS.transitionSpecialist,
    description: "Guides you through the actual transition with milestone-based support, turning your plan into reality.",
    capabilities: [
      "Milestone planning",
      "Progress tracking",
      "Accountability support",
      "Transition coordination"
    ]
  }
];

export default function MeetYourTeamPage() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden" data-testid="team-hero">
        {/* Animated Image Background with Ken Burns effect */}
        <div className="absolute inset-0">
          <motion.div
            initial={{ scale: 1 }}
            animate={{ scale: 1.1 }}
            transition={{ duration: 20, repeat: Infinity, repeatType: "reverse", ease: "linear" }}
            className="absolute inset-0"
          >
            <img
              src={TEAM_HERO_IMAGE}
              alt="Your support team led by Nia"
              className="w-full h-full object-cover object-[center_20%] sm:object-[center_25%] md:object-[center_30%] lg:object-[center_35%] xl:object-[center_40%]"
            />
          </motion.div>
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-deep-violet/60" />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-deep-violet/40 via-transparent to-deep-violet/80" />
        </div>

        {/* Floating accent orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-[1]">
          <div className="absolute top-10 left-1/4 w-72 h-72 bg-brand-peach/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-1/4 w-80 h-80 bg-brand-lavender/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-brand-peach font-medium tracking-widest uppercase text-sm mb-6"
          >
            Your Support System
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-normal text-text-light leading-tight mb-6"
            data-testid="team-title"
          >
            Meet Your <span className="gradient-text">Team</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-text-light/70 text-lg sm:text-xl max-w-3xl mx-auto"
          >
            Four specialized support roles led by a single orchestrator, coordinated around you: an Emotional Readiness Guide, Career Discovery Coach, Resource Navigator, and Transition Specialist, all working together, all affordable.
          </motion.p>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-24 bg-warm-bone" data-testid="team-grid-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="group"
                data-testid={`team-card-${index}`}
              >
                <div className="h-full p-8 rounded-3xl bg-white border border-gray-100 shadow-lg hover:shadow-xl transition-shadow">
                  {/* Portrait and Icon Row */}
                  <div className="flex items-center gap-4 mb-6">
                    {/* Portrait */}
                    <div className="relative">
                      <img 
                        src={member.portrait} 
                        alt={member.name}
                        className="w-20 h-20 rounded-full object-cover border-2 border-white shadow-md"
                      />
                      {/* Icon badge */}
                      <div className={`absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-gradient-to-br ${member.color} flex items-center justify-center shadow-md`}>
                        <member.icon className="w-4 h-4 text-white" />
                      </div>
                    </div>
                    
                    {/* Name */}
                    <h3 className="font-heading text-2xl text-text-dark">{member.name}</h3>
                  </div>

                  {/* Description */}
                  <p className="text-text-dark/70 leading-relaxed mb-6">{member.description}</p>

                  {/* Capabilities */}
                  <div className="space-y-2">
                    {member.capabilities.map((capability) => (
                      <div key={capability} className="flex items-center gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${member.color}`} />
                        <span className="text-text-dark/60 text-sm">{capability}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Nia Section */}
      <section className="py-24 bg-gradient-to-br from-deep-violet to-[#2a1b3d]" data-testid="nia-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative order-2 lg:order-1"
            >
              <div className="relative mx-auto w-80 h-80">
                {/* Orbital rings */}
                <div className="absolute inset-0 rounded-full border border-brand-peach/20 animate-pulse" />
                <div className="absolute inset-4 rounded-full border border-brand-lavender/30" />
                <div className="absolute inset-8 rounded-full border border-white/10" />
                
                {/* Center, Nia's portrait */}
                <div className="absolute inset-12 rounded-full overflow-hidden border-4 border-brand-peach/30 shadow-2xl">
                  <img 
                    src={PORTRAITS.nia} 
                    alt="Nia, Your Dedicated Guide"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Floating team icons */}
                {teamMembers.map((member, index) => {
                  const angle = (index * 90 + 45) * (Math.PI / 180);
                  const radius = 130;
                  const x = Math.cos(angle) * radius;
                  const y = Math.sin(angle) * radius;
                  
                  return (
                    <motion.div
                      key={member.name}
                      className="absolute"
                      style={{
                        left: `calc(50% + ${x}px - 20px)`,
                        top: `calc(50% + ${y}px - 20px)`,
                      }}
                      animate={{
                        y: [0, -8, 0],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        delay: index * 0.5,
                      }}
                    >
                      {/* Mini portrait with icon */}
                      <div className="relative">
                        <img 
                          src={member.portrait} 
                          alt={member.name}
                          className="w-10 h-10 rounded-full object-cover border-2 border-white/50 shadow-lg"
                        />
                        <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-gradient-to-br ${member.color} flex items-center justify-center`}>
                          <member.icon className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-peach/20 mb-6">
                <Sparkles className="w-4 h-4 text-brand-peach" />
                <span className="text-brand-peach text-sm font-medium">Your Companion</span>
              </div>

              <h2 className="font-heading text-4xl sm:text-5xl text-text-light mb-6">
                Meet <span className="gradient-text">Nia</span>
              </h2>

              <p className="text-text-light/80 text-lg leading-relaxed mb-6">
                Nia brings everything together for you. She coordinates your entire support team into one seamless experience, 
                knowing where you are in your journey, what you need next, and which team member to bring in.
              </p>

              <p className="text-text-light/60 leading-relaxed mb-8">
                Think of Nia as your personal guide. She ensures your entire team 
                works together harmoniously, so you never have to manage multiple tools or repeat yourself. 
                One conversation, complete support.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-brand-peach/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2 h-2 rounded-full bg-brand-peach" />
                  </div>
                  <p className="text-text-light/70">Remembers your complete story and context</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-brand-peach/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2 h-2 rounded-full bg-brand-peach" />
                  </div>
                  <p className="text-text-light/70">Seamlessly brings in the right support when you need it</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-brand-peach/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2 h-2 rounded-full bg-brand-peach" />
                  </div>
                  <p className="text-text-light/70">Keeps your journey on track</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Transition to Why Become */}
      <section className="py-20 bg-gradient-to-b from-warm-bone to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <p className="text-brand-primary font-medium tracking-widest uppercase text-sm mb-4">
              What Sets Us Apart
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl text-text-dark mb-6">
              Discover Why Become Is Different
            </h2>
            <p className="text-text-dark/60 text-lg mb-8 max-w-2xl mx-auto">
              Learn about the principles, privacy-first approach, and clinical foundation that make Become unique.
            </p>
            <Link to="/why-become">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-brand-peach to-brand-lavender text-deep-violet hover:opacity-90"
                data-testid="why-become-transition-btn"
              >
                Discover Why Become
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Final CTA, Signup */}
      <section className="py-32 hero-dark" data-testid="team-final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-3xl sm:text-4xl text-text-light mb-6">
              Ready to Meet Your Team?
            </h2>
            <p className="text-text-light/70 text-lg mb-10 max-w-2xl mx-auto">
              Join the waitlist and be among the first to experience coordinated support that's built around you.
            </p>
            <Link to="/contact">
              <Button
                className="h-12 px-8 rounded-full bg-gradient-to-r from-brand-peach to-brand-lavender text-deep-violet font-semibold hover:shadow-[0_0_30px_rgba(245,168,130,0.5)] transition-all hover:-translate-y-0.5"
                data-testid="signup-team-btn"
              >
                Join the Waitlist
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
